import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Box
} from '@material-ui/core';
import { Elements } from '@stripe/react-stripe-js';
import { useStripe } from '../contexts/StripeContext';
import api from '../utils/api';
import PaymentForm from '../components/invoice/PaymentForm';

const InvoicePayPage = () => {
  const { id } = useParams();
  const history = useHistory();
  const { t } = useTranslation();
  const { stripePromise } = useStripe();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [options, setOptions] = useState(null);
  const language = useSelector(state => state.app.language);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const invoiceData = await api.user.getInvoice(id);
        const currentInvoice = invoiceData.invoice;

        if (['validated', 'partially_paid'].includes(currentInvoice.status)) {
          const response = await api.user.getInvoiceStripePaymentElements(id);
          setData(response);

          if (
            response.stripe_client_secret &&
            response.stripe_customer_session_client_secret
          ) {
            setOptions({
              clientSecret: response.stripe_client_secret,
              customerSessionClientSecret:
                response.stripe_customer_session_client_secret,
              appearance: {
                theme: 'stripe'
              },
              locale: language
            });
          }
        } else {
          setData({ invoice: currentInvoice });
        }

        setError(null);
      } catch (err) {
        setError(err.message || t('invoice:loading_error'));
        console.error('Error fetching invoice data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id, language]);

  if (loading) {
    return (
      <Box p={3}>
        <Typography variant="h4">{t('invoice:loading')}</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography variant="h4" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  const invoice = data.invoice || {};
  const invoiceElements = invoice.invoice_elements || [];

  const renderPaymentContent = () => {
    if (
      invoice.status === 'paid' ||
      !['validated', 'partially_paid'].includes(invoice.status)
    ) {
      const isPaid = invoice.status === 'paid';
      const isPending = invoice.status === 'pending';

      let message;
      if (isPaid) {
        message = t('invoice:payment_already_completed');
      } else if (isPending) {
        message = t('invoice:payment_pending_validation');
      } else {
        message = t('invoice:payment_processing_message');
      }

      const color = isPaid ? 'primary' : 'initial';

      return (
        <Box p={3}>
          <Typography variant="h6" color={isPaid ? 'primary' : 'initial'}>
            {message}
          </Typography>
          <Box display="flex" justifyContent="center" mt={3}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => history.push('/frontofficev2/')}
            >
              {t('invoice:return')}
            </Button>
          </Box>
        </Box>
      );
    } else if (stripePromise && options) {
      return (
        <Paper style={{ padding: '20px' }}>
          <Elements stripe={stripePromise} options={options}>
            <PaymentForm
              clientSecret={options.clientSecret}
              customerSessionClientSecret={options.customerSessionClientSecret}
              returnUrl={
                window.location.origin +
                `/frontofficev2/invoices/payment/status?invoice_id=${id}`
              }
            />
          </Elements>
        </Paper>
      );
    }
    return null;
  };

  return (
    <Box p={3}>
      <Paper style={{ padding: '20px', marginBottom: '20px' }}>
        <Typography variant="h4" component="h2" gutterBottom>
          {invoice.name}
        </Typography>
        {invoice.comment && (
          <Typography variant="h6" gutterBottom>
            {invoice.comment}
          </Typography>
        )}

        <Box mt={3}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>{t('invoice:description')}</strong>
                </TableCell>
                <TableCell>
                  <strong>{t('invoice:unit_price')}</strong>
                </TableCell>
                <TableCell>
                  <strong>{t('invoice:quantity')}</strong>
                </TableCell>
                <TableCell>
                  <strong>{t('invoice:price')}</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoiceElements.map((element, index) => (
                <TableRow key={index}>
                  <TableCell>{element.description}</TableCell>
                  <TableCell>{element.unit_price}</TableCell>
                  <TableCell>{element.quantity}</TableCell>
                  <TableCell>{element.price}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={3}>
                  <strong>{t('invoice:total')}</strong>
                </TableCell>
                <TableCell>
                  <strong>{invoice.price}</strong>
                </TableCell>
              </TableRow>
              {invoice.to_pay_price && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <strong>{t('invoice:to_pay')}</strong>
                  </TableCell>
                  <TableCell>
                    <strong>{invoice.to_pay_price}</strong>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      {renderPaymentContent()}

      {['validated', 'partially_paid'].includes(invoice.status) && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => history.push('/frontofficev2/')}
          >
            {t('invoice:return')}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default InvoicePayPage;
